import { HTTPClient } from "@abaco/web-common/src/HTTP";
import {
    AdeError,
    AdeResponse,
    CaptchaAuthAde,
    Council,
    CouncilAde,
    Parcel,
    ParcelAde,
    ParcelGeometry,
    ParcelGeometryAde,
    Province,
    ProvinceAde,
    Sheet,
    SheetAde
} from "../models/Parcels";
// import https from "https";

export default class ParcelApi {
    private http: HTTPClient;
    private unAuthHttp!: HTTPClient;

    constructor(http: HTTPClient) {
        this.http = http;
        this.unAuthHttp = new HTTPClient();
    }

    private contextMappings: Record<string, string> = {
        FARM_MAP: "ACTIVE_LAYERS",
        FARM_CROP_PLAN_MAP: "ACTIVE_LAYERS"
    };

    private getContext(context: string): string {
        return this.contextMappings[context] || context;
    }

    public async getRegionProvinces(regionId: string): Promise<AdeResponse<Province[]>> {
        // const res = await this.http.get("/ade/inspire/ajax/ajax.php", {
        //     params: {
        //         op: "getProvince",
        //         reg: regionId,
        //         tkn: ""
        //     }
        // });
        const res = await this.makeXhrGetRequest("/ade/inspire/ajax/ajax.php?op=getProvince&reg=" + regionId + "&tkn=");
        const { hasError, errorCode } = this.checkAdeError(res);
        if (hasError) {
            return {
                error: true,
                errorCode: errorCode,
                data: []
            };
        } else {
            // example of API response: {"SIGLA_PROVINCIA":["MT"],"DENOMINAZIONE":["MATERA"]}
            // id: SIGLA_PROVINCIA, label: DENOMINAZIONE
            const data = res as ProvinceAde;
            const formattedRes = data.SIGLA_PROVINCIA.map((id: string, index: number) => {
                return {
                    id,
                    label: data.DENOMINAZIONE[index]
                };
            });
            return {
                error: false,
                errorCode: null,
                data: formattedRes
            };
        }
    }

    // private testDone = false;
    public async getProvinceCouncils(provinceId: string): Promise<AdeResponse<Council[]>> {
        const res = await this.http.get("/ade/inspire/ajax/ajax.php", {
            params: {
                op: "getComuniSez",
                prov: provinceId,
                tkn: ""
            }
        });
        const { hasError, errorCode } = this.checkAdeError(res);
        // console.log("to remove before commit");
        // let { hasError, errorCode } = this.checkAdeError(res);
        // if (!this.testDone) {
        //     this.testDone = true;
        //     hasError = true;
        //     errorCode = "tokenExpired";
        // }
        if (hasError) {
            return {
                error: true,
                errorCode: errorCode,
                data: []
            };
        } else {
            // example of API response: {"COD_COMUNE":["A017"],"DENOMINAZIONE":["ACCETTURA"], "NODISPLAY": ["0"], SEZIONE":["A"]}
            // councilCode: COD_COMUNE, label: DENOMINAZIONE, section: SEZIONE, noDisplay: NODISPLAY
            const data = res.data as CouncilAde;
            const formattedRes = data.COD_COMUNE.map((councilCode: string, index: number) => {
                return {
                    councilCode,
                    label: data.DENOMINAZIONE[index],
                    section: data.SEZIONE[index],
                    noDisplay: data.NODISPLAY[index] === "1" ? true : false
                };
            });
            return {
                error: false,
                errorCode: null,
                data: formattedRes
            };
        }
    }

    public async getCouncilSheets(
        provinceId: string,
        councilCode: string,
        token: string
    ): Promise<AdeResponse<Sheet[]>> {
        const res = await this.http.get("/ade/inspire/ajax/ajax.php", {
            params: {
                op: "getFogli",
                prov: provinceId,
                cod_com: councilCode,
                tkn: token
            }
        });
        const { hasError, errorCode } = this.checkAdeError(res);
        if (hasError) {
            return {
                error: true,
                errorCode: errorCode,
                data: []
            };
        } else {
            // example of API response: {"FOGLIO":["1"], "NODISPLAY": ["0"]}
            // id: FOGLIO, noDisplay: NODISPLAY, label: FOGLIO
            const data = res.data as SheetAde;
            const formattedRes = data.FOGLIO.map((id: string, index: number) => {
                return {
                    id,
                    noDisplay: data.NODISPLAY[index] === "1" ? true : false,
                    label: id
                };
            });
            return {
                error: false,
                errorCode: null,
                data: formattedRes
            };
        }
    }

    public async getSheetParcels(
        provinceId: string,
        councilCode: string,
        sheetId: string,
        token: string
    ): Promise<AdeResponse<Parcel[]>> {
        const res = await this.http.get("/ade/inspire/ajax/ajax.php", {
            params: {
                op: "getParts",
                prov: provinceId,
                cod_com: councilCode,
                foglio: sheetId,
                tkn: token
            }
        });
        const { hasError, errorCode } = this.checkAdeError(res);
        if (hasError) {
            return {
                error: true,
                errorCode: errorCode,
                data: []
            };
        } else {
            // example of API response: {"CODICE":["A"], "NODISPLAY": ["0"], NUM_PART":["A/"]}
            // id: NUM_PART, noDisplay: NODISPLAY, label: CODICE
            const data = res.data as ParcelAde;
            const formattedRes = data.NUM_PART.map((id: string, index: number) => {
                return {
                    id,
                    noDisplay: data.NODISPLAY[index] === "1" ? true : false,
                    label: data.CODICE[index]
                };
            });
            return {
                error: false,
                errorCode: null,
                data: formattedRes
            };
        }
    }

    public async getParcelDetail(
        provinceId: string,
        councilCode: string,
        sheetId: string,
        parcelId: string,
        token: string
    ): Promise<AdeResponse<ParcelGeometry | null>> {
        const res = await this.http.get("/ade/inspire/ajax/ajax.php", {
            params: {
                op: "getGeomPart",
                prov: provinceId,
                cod_com: councilCode,
                foglio: sheetId,
                num_part: parcelId,
                tkn: token
            }
        });
        const { hasError, errorCode } = this.checkAdeError(res);
        if (hasError) {
            return {
                error: true,
                errorCode: errorCode,
                data: null
            };
        } else {
            // example of API response: {"GEOMETRIA": ["{ \"type\": \"Polygon\", \"coordinates\": [ [ [13.66, 42.20], [13.66, 42.20], [13.66, 42.20]] }"]}
            const data = res.data as ParcelGeometryAde;
            const dataJson = JSON.parse(data.GEOMETRIA[0]);
            const coordinates: ParcelGeometry = { coordinates: dataJson.coordinates[0] };
            return {
                error: false,
                errorCode: null,
                data: coordinates
            };
        }
    }

    public async getCaptchaImage(): Promise<any | null> {
        try {
            const resNew: any = await this.makeXhrGetRequest("/age-inspire/srv/ita/Captcha?type=image&lang=ita", true);
            return resNew;
        } catch (error) {
            return null;
        }
    }

    public async authorizeCaptcha(captcha: string): Promise<{ authorized: boolean; token: string | null }> {
        const res = await this.http.get("/age-inspire/srv/ita/Captcha", {
            params: {
                type: "check",
                captcha: captcha,
                tkn: ""
            }
        });
        // example of API response: {"message": "", result: true, token: "ASD123"}
        const data = res.data as CaptchaAuthAde;
        if (data.result === true && data.token && data.token.length > 0) {
            return { authorized: true, token: data.token };
        } else {
            return { authorized: false, token: null };
        }
    }

    private checkAdeError(res: any): { hasError: boolean; errorCode: AdeError | null } {
        let hasError = false;
        let errorCode: AdeError | null = null;
        if (res && res.data) {
            if (typeof res.data === "string") {
                if (res.data.includes("chiamata non autorizzata")) {
                    hasError = true;
                    errorCode = "unauthorized";
                } else if (res.data.includes("tk expired")) {
                    hasError = true;
                    errorCode = "tokenExpired";
                } else if (res.data.includes("ERROR")) {
                    hasError = true;
                    errorCode = "generic";
                }
            }
        } else if (res && typeof res === "string") {
            if (res.includes("chiamata non autorizzata")) {
                hasError = true;
                errorCode = "unauthorized";
            } else if (res.includes("tk expired")) {
                hasError = true;
                errorCode = "tokenExpired";
            } else if (res.includes("ERROR")) {
                hasError = true;
                errorCode = "generic";
            }
        } else if (!res) {
            hasError = true;
            errorCode = "generic";
        }
        return { hasError, errorCode };
    }

    private async makeFetchGetRequest(url: string) {
        const options = {
            method: "GET",
            headers: {
                Accept: "application/json, text/plain, */*",
                "Accept-Encoding": "gzip, deflate, br",
                Connection: "keep-alive",
                "Cache-Control": "no-cache"
            }
        };
        try {
            const res = await fetch(url, options);
            return res;
        } catch (error) {
            return null;
        }
    }

    private makeXhrGetRequest(url: string, isBlob = false) {
        return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhr.open("GET", url);
            if (isBlob) {
                xhr.responseType = "blob";
            }
            xhr.onload = function() {
                if (xhr.status >= 200 && xhr.status < 300) {
                    if (isBlob) {
                        const reader = new FileReader();
                        reader.readAsDataURL(xhr.response);
                        reader.onload = function() {
                            resolve(reader.result);
                        };
                    } else {
                        // transform in json
                        const json = JSON.parse(xhr.response);
                        resolve(json);
                    }
                } else {
                    reject(new Error("Request failed: " + xhr.statusText));
                }
            };
            xhr.onerror = function() {
                reject(new Error("Network error"));
            };
            xhr.send();
        });
    }
}
